import { Colors, Slider, Text } from '@amelio/ui-library';

import { COMPENSATION_CAPTION, COMPENSATION_TILE_SURFACE } from '../compensationStyles';
import { LevelIcon } from './LevelIcon';

import type { CompensationLevel } from '@/types/compensation';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getLocalizedText } from '@/utils/localization';
import { formatDecimal, formatPercent } from '@/utils/numberFormat';

/** 0 excludes the level entirely; 3 is the ceiling the backend validates against. */
const MULTIPLIER_MIN = 0;
const MULTIPLIER_MAX = 3;
const MULTIPLIER_STEP = 0.1;

interface LevelTileProps {
    level: CompensationLevel;
    /** The multiplier in force: the reader's own override, or the level's default. */
    multiplier: number;
    /** People of this level the calculation could include, from the server's level summary. */
    employeeCount: number;
    /** The percentage of salary this level's multiplier currently buys — read off any of its
     * computed lines, since in `Level` mode every eligible person of a level gets the same
     * proposed percentage. `null` when the level has nobody the calculation could include. */
    shareOfSalary: number | null;
    canEdit: boolean;
    onMultiplierChange: (value: number) => void;
}

/** One performance level in `Level` distribution mode: what it is, how many people are in it, and
 * the multiplier that decides its share of the envelope. */
export const LevelTile = ({
    level,
    multiplier,
    employeeCount,
    shareOfSalary,
    canEdit,
    onMultiplierChange,
}: LevelTileProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const label = getLocalizedText(level.label);

    return (
        <div
            className={`${COMPENSATION_TILE_SURFACE} flex flex-col gap-1`}
            data-testid={`compensation-level-tile-${level.id}`}
        >
            <div className={'flex items-start gap-2'}>
                {/* The level's own mark and colour, the same the pill carries in the adjustments
                    table, so a tile is recognisable before its label is read. */}
                <span className={'mt-0.5 flex-shrink-0'}>
                    <LevelIcon level={level} />
                </span>

                <Text
                    weight={'bold'}
                    color={Colors.NAVY}
                    className={COMPENSATION_CAPTION}
                >
                    {label}
                </Text>
            </div>

            <Text
                color={Colors.BASE_500}
                className={COMPENSATION_CAPTION}
            >
                {t('compensation.strategyCard.level.headcount', { count: employeeCount })}
            </Text>

            <div className={`mt-[12px] flex items-baseline gap-1.5`}>
                <Text
                    color={Colors.NAVY}
                    className={`text-[24px] font-bold tabular-nums`}
                >
                    {formatDecimal(multiplier, language)}
                </Text>

                <Text
                    weight={'semibold'}
                    color={Colors.BASE_500}
                    className={COMPENSATION_CAPTION}
                >
                    {t('compensation.strategyCard.level.timesAverage')}
                </Text>
            </div>

            {/* Kept on screen when the envelope is read-only rather than removed: where the thumb
                sits is half of what the multiplier means, and a closed envelope is still read. */}
            <Slider
                trackTone={'light'}
                minValue={MULTIPLIER_MIN}
                maxValue={MULTIPLIER_MAX}
                step={MULTIPLIER_STEP}
                currentValue={multiplier}
                onChange={onMultiplierChange}
                showScale={false}
                ariaLabel={label}
                disabled={!canEdit}
                dataTestId={`compensation-level-multiplier-${level.id}`}
            />

            {shareOfSalary === null ? null : (
                <Text
                    color={Colors.BASE_500}
                    className={COMPENSATION_CAPTION}
                >
                    {t('compensation.strategyCard.level.shareOfSalary', {
                        ratio: formatPercent(shareOfSalary, language, { fractionDigits: 2 }),
                    })}
                </Text>
            )}
        </div>
    );
};
